import { apiRequest } from "@/lib/queryClient";
import {
  DEFAULT_MAX_UPLOAD_BYTES,
  DEFAULT_MAX_UPLOAD_LABEL,
  DEFAULT_MAX_MEDIA_UPLOAD_BYTES,
  DEFAULT_MAX_MEDIA_UPLOAD_LABEL,
  formatUploadBytes,
  getOversizedFileMessage,
} from "@shared/upload-limits";

export {
  DEFAULT_MAX_UPLOAD_BYTES,
  DEFAULT_MAX_UPLOAD_LABEL,
  DEFAULT_MAX_MEDIA_UPLOAD_BYTES,
  DEFAULT_MAX_MEDIA_UPLOAD_LABEL,
  formatUploadBytes,
  getOversizedFileMessage,
};

// ---------------------------------------------------------------------------
// Live upload-cap synchronization with the server (Task #775).
//
// `maxBytes` = general document ceiling (default 250 MB).
// `maxMediaBytes` = media ceiling for video/audio (default 2 GiB).
// ---------------------------------------------------------------------------
type UploadLimits = { maxBytes: number; maxMediaBytes: number };

let cachedLimits: UploadLimits | null = null;
let inflightCapFetch: Promise<UploadLimits> | null = null;

async function loadUploadLimits(): Promise<UploadLimits> {
  if (cachedLimits !== null) return cachedLimits;
  if (inflightCapFetch) return inflightCapFetch;
  inflightCapFetch = (async () => {
    try {
      const res = await fetch("/api/uploads/limits", { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as {
        maxBytes?: unknown;
        maxMediaBytes?: unknown;
      };
      const maxBytes =
        typeof body?.maxBytes === "number" && Number.isFinite(body.maxBytes) && body.maxBytes > 0
          ? Math.floor(body.maxBytes)
          : DEFAULT_MAX_UPLOAD_BYTES;
      const maxMediaBytes =
        typeof body?.maxMediaBytes === "number" &&
        Number.isFinite(body.maxMediaBytes) &&
        body.maxMediaBytes > 0
          ? Math.floor(body.maxMediaBytes)
          : DEFAULT_MAX_MEDIA_UPLOAD_BYTES;
      cachedLimits = { maxBytes, maxMediaBytes };
      return cachedLimits;
    } catch {
      cachedLimits = {
        maxBytes: DEFAULT_MAX_UPLOAD_BYTES,
        maxMediaBytes: DEFAULT_MAX_MEDIA_UPLOAD_BYTES,
      };
      return cachedLimits;
    } finally {
      inflightCapFetch = null;
    }
  })();
  return inflightCapFetch;
}

export async function loadUploadCap(): Promise<number> {
  return (await loadUploadLimits()).maxBytes;
}

export async function loadMediaUploadCap(): Promise<number> {
  return (await loadUploadLimits()).maxMediaBytes;
}

export function getCachedUploadCap(): number {
  return cachedLimits?.maxBytes ?? DEFAULT_MAX_UPLOAD_BYTES;
}

export function getCachedMediaUploadCap(): number {
  return cachedLimits?.maxMediaBytes ?? DEFAULT_MAX_MEDIA_UPLOAD_BYTES;
}

/**
 * Test-only escape hatch. Resets the in-memory cap cache so each spec
 * starts from a clean slate; never call from production code.
 */
export function __resetUploadCapCacheForTests(): void {
  cachedLimits = null;
  inflightCapFetch = null;
}

/**
 * Async pre-flight check that uses the *server's* effective general cap.
 */
export async function getOversizedFileMessageAsync(
  file: { name: string; size: number },
): Promise<string | null> {
  const max = await loadUploadCap();
  return getOversizedFileMessage(file, max);
}

export async function putFileToPresignedUrl(
  file: File,
  uploadURL: string,
  onProgress?: (pct: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadURL);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      const detail = (xhr.responseText || "").slice(0, 200).trim();
      reject(
        new Error(
          `Storage rejected the upload (HTTP ${xhr.status})${detail ? `: ${detail}` : ""}`,
        ),
      );
    };
    xhr.onerror = () => {
      // `xhr.onerror` fires for connection-level failures: network
      // down, DNS failure, TLS reject, or — most commonly in
      // production — a cross-origin (CORS) preflight rejection from
      // the storage host. It does NOT fire for HTTP 4xx/5xx, which
      // hit `xhr.onload` above. Surface the storage host so the
      // operator can immediately tell whether the browser even got
      // to the right place. See docs/supabase-storage-runbook.md
      // "CORS configuration on the Supabase project" for the fix.
      let host = "the storage host";
      try {
        host = new URL(uploadURL).host;
      } catch {
        /* keep generic */
      }
      reject(
        new Error(
          `Couldn't reach storage (${host}). This is usually a network issue or a storage CORS misconfiguration — try again, and if it keeps failing, contact support.`,
        ),
      );
    };
    xhr.ontimeout = () => reject(new Error("Storage upload timed out"));
    // 0 = no timeout. A ~1–2 GB deal-room video can take many minutes on
    // slower admin networks; a default browser timeout would abort mid-PUT
    // and look like an "upload cap" failure.
    xhr.timeout = 0;
    xhr.send(file);
  });
}

// Keep this list in sync with ALLOWED_EXTENSIONS in server/object-uploads.ts.
// The server is the source of truth; this client-side check just gives users
// fast feedback before we hit the network.
export const ALLOWED_UPLOAD_EXTENSIONS = [
  ".pdf", ".doc", ".docx", ".xls", ".xlsx",
  ".numbers", ".pages", ".key",
  ".png", ".jpg", ".jpeg", ".webp",
] as const;

export const ALLOWED_UPLOAD_TYPES_DESCRIPTION =
  "PDF, Word, Excel, Apple Numbers/Pages/Keynote, PNG, JPG, WEBP";

function getExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot).toLowerCase() : "";
}

/**
 * Returns a friendly error string when `file` doesn't match the allowed
 * extension list, or `null` when the file looks acceptable. The server still
 * does the authoritative validation; this just lets us reject obvious
 * mismatches before requesting a presigned URL.
 */
export function getDisallowedFileTypeMessage(
  file: File,
  allowedExtensions: readonly string[] = ALLOWED_UPLOAD_EXTENSIONS,
  description: string = ALLOWED_UPLOAD_TYPES_DESCRIPTION,
): string | null {
  const ext = getExtension(file.name);
  if (!ext) {
    return `File "${file.name}" has no extension. Allowed types: ${description}.`;
  }
  if (!allowedExtensions.includes(ext)) {
    return `File type "${ext}" isn't supported. Allowed types: ${description}.`;
  }
  return null;
}

export function extractServerErrorMessage(err: unknown, fallback = "Upload failed"): string {
  const raw = err instanceof Error ? err.message : String(err);
  const m = raw.match(/^\d+:\s*(.*)$/);
  let msg = m ? m[1] : raw;
  try {
    const parsed: unknown = JSON.parse(msg);
    if (
      parsed &&
      typeof parsed === "object" &&
      "error" in parsed &&
      typeof (parsed as { error: unknown }).error === "string"
    ) {
      msg = (parsed as { error: string }).error;
    }
  } catch {
    // not JSON, leave as-is
  }
  return msg || fallback;
}

/**
 * Translate a raw upload-rejection message into reassuring, plain-English
 * copy when the failure was a transient scanner cold-start (Task #1858).
 *
 * The server rejects an unscannable upload with "... could not be scanned
 * and was rejected ...". On our hosted setup the most common cause by far
 * is the malware scanner waking from idle, which resolves on its own within
 * a minute or two. Surfacing the raw technical reason reads as "the platform
 * is broken" to a brand-new investor; this softens it into a retry prompt.
 *
 * A genuine `infected` verdict ("failed virus scan") is left untouched —
 * that is a real rejection the investor should see plainly.
 *
 * Returns `{ message, transient }` so callers can also soften the toast
 * style (informational rather than destructive) for the warming-up case.
 */
export function friendlyUploadErrorMessage(raw: string): { message: string; transient: boolean } {
  const lower = raw.toLowerCase();
  if (lower.includes("could not be scanned")) {
    return {
      message:
        "Our security scanner is just starting up and couldn't check your file in time. " +
        "This is temporary — please wait a moment and try uploading again.",
      transient: true,
    };
  }
  return { message: raw, transient: false };
}

export async function requestPresignedUploadUrl(
  file: File,
): Promise<{ uploadURL: string; objectPath: string }> {
  const res = await fetch("/api/uploads/request-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      name: file.name,
      size: file.size,
      contentType: file.type || "application/octet-stream",
    }),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.json();
      if (body && typeof body.error === "string") detail = body.error;
    } catch {
      try {
        detail = (await res.text()).slice(0, 200);
      } catch {
        // ignore
      }
    }
    throw new Error(
      `Could not request upload URL (HTTP ${res.status})${detail ? `: ${detail}` : ""}`,
    );
  }
  return res.json();
}

export interface DirectUploadFinalizePayload {
  objectPath: string;
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface UploadFileToStorageOptions {
  /**
   * When the bytes that should be sent to storage differ from the source file
   * (e.g. after HEIC->JPEG conversion in the browser), pass the transcoded
   * file here. Defaults to the source file.
   */
  uploadFile?: File;
  onProgress?: (pct: number) => void;
  /**
   * Override the default presign endpoint (`/api/uploads/request-url`). Used
   * by flows that have their own scoped presign endpoint, e.g. project photos.
   */
  requestUploadUrl?: (file: File) => Promise<{ uploadURL: string; objectPath: string }>;
  /** Override the file name reported to the finalize endpoint. */
  fileName?: string;
  /** Override the content type reported to the finalize endpoint. */
  contentType?: string;
  /** Override the file size reported to the finalize endpoint. */
  fileSize?: number;
  /**
   * Override the pre-flight size ceiling (defaults to the general
   * `/api/uploads/limits` `maxBytes`). Content-board media uploaders pass
   * the media ceiling so a ~1–2 GB MP4 is not rejected client-side.
   */
  maxBytes?: number;
}

/**
 * Step 1+2 of the direct-upload flow: ask the server for a presigned URL,
 * PUT the file's bytes to it, and return the metadata envelope every
 * `finalizeDirectUpload`-backed endpoint expects.
 *
 * Use this when you need to upload several files in one batch and then make
 * a single finalize call (e.g. investor verification). Most callers should
 * prefer `uploadAndFinalize`, which also performs the finalize POST.
 */
export async function uploadFileToStorage(
  sourceFile: File,
  opts: UploadFileToStorageOptions = {},
): Promise<DirectUploadFinalizePayload> {
  const presign = opts.requestUploadUrl ?? requestPresignedUploadUrl;
  const fileToPut = opts.uploadFile ?? sourceFile;
  // Pre-flight size guard so a user who picks a too-large file gets a
  // friendly error before we call the presign endpoint or PUT any bytes.
  // We resolve the cap from the server (cached after first hit) so we
  // honor any `UPLOAD_MAX_SCANNABLE_BYTES` env override and never
  // disagree with the authoritative server check (Task #759, Task #775).
  // We check both files because a HEIC->JPEG transcode can produce a
  // different size than the source.
  const maxBytes = opts.maxBytes ?? (await loadUploadCap());
  const sizeError =
    getOversizedFileMessage(sourceFile, maxBytes) ??
    (fileToPut !== sourceFile ? getOversizedFileMessage(fileToPut, maxBytes) : null);
  if (sizeError) throw new Error(sizeError);
  const { uploadURL, objectPath } = await presign(sourceFile);
  await putFileToPresignedUrl(fileToPut, uploadURL, opts.onProgress);
  return {
    objectPath,
    fileName: opts.fileName ?? fileToPut.name,
    contentType: opts.contentType ?? (fileToPut.type || "application/octet-stream"),
    fileSize: opts.fileSize ?? fileToPut.size,
  };
}

export interface UploadAndFinalizeOptions extends UploadFileToStorageOptions {
  /** The source file the user picked. */
  file: File;
  /** Server endpoint that calls `finalizeDirectUpload` with the upload payload. */
  finalizeUrl: string;
  finalizeMethod?: "POST" | "PUT" | "PATCH";
  /**
   * Extra fields merged into the JSON body posted to `finalizeUrl`. The
   * standard `{ objectPath, fileName, contentType, fileSize }` envelope is
   * always included automatically.
   */
  extraBody?: Record<string, unknown>;
  /**
   * Fallback toast/error message used when the server response can't be
   * parsed for a more specific error.
   */
  finalizeErrorFallback?: string;
}

/**
 * Run the full direct-upload flow for a single admin upload:
 *
 *   1. Request a presigned upload URL from the server.
 *   2. PUT the file's bytes to that URL.
 *   3. POST the finalize payload to `finalizeUrl`, merging any caller-provided
 *      `extraBody` fields with the standard
 *      `{ objectPath, fileName, contentType, fileSize }` envelope every
 *      `finalizeDirectUpload`-backed endpoint expects.
 *
 * Errors thrown by the server during the finalize step are re-thrown with the
 * server's specific message (rather than a generic toast string) so admins
 * can see exactly why the upload was rejected — this is the precondition /
 * magic-mime fix for the executed term sheet flow generalized to every
 * admin upload that hits `finalizeDirectUpload`.
 *
 * Returns the parsed JSON body of the finalize response, or `undefined` when
 * the endpoint returned no body.
 */
export async function uploadAndFinalize<T = unknown>(
  opts: UploadAndFinalizeOptions,
): Promise<T | undefined> {
  const meta = await uploadFileToStorage(opts.file, opts);
  let res: Response;
  try {
    res = await apiRequest(opts.finalizeMethod ?? "POST", opts.finalizeUrl, {
      ...meta,
      ...(opts.extraBody ?? {}),
    });
  } catch (err) {
    throw new Error(
      extractServerErrorMessage(err, opts.finalizeErrorFallback ?? "Server rejected the file"),
    );
  }
  try {
    return (await res.json()) as T;
  } catch {
    return undefined;
  }
}
